import { type GridPaginationModel, type GridSortModel } from '@mui/x-data-grid-pro';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { type RowsPerPage, RowsPerPageSchema } from './utils';
import { BrowserStorageVariant, useBrowserStorage } from '../../browserStorage/useBrowserStorage';

export interface TablePaginationProps {
  paginationModel: GridPaginationModel;
  setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
  goToFirstPage: () => void;
}

export interface TableSortProps {
  sortModel: GridSortModel;
  setSortModel: (sortModel: GridSortModel) => void;
}

export interface UseStateAwareTableSortProps {
  sortModel: GridSortModel;
  setSortModel: Dispatch<SetStateAction<GridSortModel>>;
}

interface UseTableStateProps {
  localStorageKeySuffix?: string;
  defaultPageSize?: RowsPerPage;
  defaultSortModel?: GridSortModel;
  rowsPerPageSchema?: z.ZodSchema<number>;
}

export function useTableState(props?: UseTableStateProps) {
  const {
    localStorageKeySuffix,
    defaultPageSize,
    defaultSortModel,
    rowsPerPageSchema = RowsPerPageSchema,
  } = props ?? {};
  const { storedValues, updateStoredValue } = useBrowserStorage({
    // `Staff:` prefix is kept verbatim so staff users do not lose their saved page size.
    storageKeyName: `Staff:TableState:${localStorageKeySuffix ?? 'Global'}`,
    variant: BrowserStorageVariant.LocalStorage,
    schema: z.object({
      pageSize: rowsPerPageSchema,
    }),
    initialValue: {
      pageSize: defaultPageSize ?? 100,
    },
  });
  const { pageSize } = storedValues;
  const [page, setPage] = useState<GridPaginationModel['page']>(0);

  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel ?? []);

  const paginationModel = useMemo<GridPaginationModel>(() => {
    return {
      pageSize,
      page,
    };
  }, [page, pageSize]);

  const setPaginationModel = useCallback(
    (modelOrUpdateFunction: GridPaginationModel | SetStateAction<GridPaginationModel>) => {
      if (typeof modelOrUpdateFunction === 'function') {
        setPage((prevPage) => {
          const prevModel = {
            pageSize,
            page: prevPage,
          };
          const newModel = modelOrUpdateFunction(prevModel);
          if (newModel.pageSize !== undefined) {
            updateStoredValue('pageSize', newModel.pageSize);
          }
          return newModel.page;
        });
        return;
      }
      setPage(modelOrUpdateFunction.page);
      updateStoredValue('pageSize', modelOrUpdateFunction.pageSize);
    },
    [pageSize, updateStoredValue]
  );
  const pageOffset = page * pageSize;

  const goToFirstPage = useCallback(() => {
    setPage(0);
  }, []);
  const pagination: TablePaginationProps = {
    paginationModel,
    setPaginationModel,
    goToFirstPage,
  };

  const sort: UseStateAwareTableSortProps = {
    sortModel,
    setSortModel,
  };

  return {
    pagination,
    paginationModel,
    sort,
    setPaginationModel,
    ...paginationModel,
    pageOffset,
    goToFirstPage,
  };
}
